import React, { lazy, Suspense, useCallback, useEffect, useState, useRef, memo } from "react";
import { UserFormData } from "../../Models/UserFormData";
import { ProfileType } from "../../Models/ProfileType";
import { TermsAndConditionsData } from "../../Models/TermsAndConditionsData";
import { toast } from "react-toastify";
import { useAppContext } from "../../../Context/AppContext";
import { Button } from "react-bootstrap";
import { BsFloppy } from "react-icons/bs";
import { useGetMITerms } from "../../../api/queries/MuslimInvest/useGetMITerms";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetCompanyDetails } from "../../../api/queries/MuslimInvest/useGetCompanyDetails";
import { useSaveCompanyProfile } from "../../../api/mutations/MuslimInvest/useSaveCompanyProfile";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { CompanyTermsTypes } from "../../Models/CompanyTerms";
import { useSaveCompanyTerms } from "../../../api/mutations/MuslimInvest/useSaveCompanyTerms";
import { useUploadCertificationPdfs } from "../../../api/mutations/MuslimInvest/useUploadCertificationPdfs";
import html2pdf from "html2pdf.js";
import { useSendEmailWithAttachments } from "../../../api/mutations/useSendEmailWithAttachments";
import { useTranslation } from "react-i18next";
import { useProfileShellLabels } from "../useMuslimInvestFormLabels";
import MuslimInvestFormHeaderBack from "../MuslimInvestFormHeaderBack";

const Company = lazy(() => import("./Company"));
const CompanyTerms = lazy(() => import("./CompanyTerms"));

function formatDate(dateString: string): string {
  if (!dateString) return "";
  const d = new Date(dateString);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

const TERMS_REF_STYLE = {
  maxHeight: "54vh",
  overflowY: "auto" as const,
  border: "1px solid #ccc",
  borderRadius: "6px",
  padding: "10px",
  background: "#fafafa",
  marginBottom: "10px",
};

const PDF_OPT = {
  margin: 8,
  filename: "company-terms.pdf",
  image: { type: "jpeg" as const, quality: 0.95 },
  html2canvas: { scale: 2, useCORS: true },
  jsPDF: {
    unit: "mm" as const,
    format: "a4" as const,
    orientation: "portrait" as const,
  },
  pagebreak: { mode: ["css", "legacy"] as const },
};

const LAZY_FALLBACK_STYLE_200 = { minHeight: 200 };
const LAZY_FALLBACK_STYLE_120 = { minHeight: 120 };

const CompanyProfile = memo(function CompanyProfile() {
  const { t } = useTranslation();
  const { tt: shellT } = useProfileShellLabels();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPendingEdit =
    (location.state as { fromPendingEdit?: boolean } | null)?.fromPendingEdit ===
    true;
  const [loading, setLoading] = useState(false);
  const { user: userData } = useAppContext();
  const [activeTab, setActiveTab] = useState(
    fromPendingEdit ? "profile-company-details" : "profile",
  );
  const [isEditingProfileCompanyDetails, setIsEditingProfileCompanyDetails] =
    useState(true);

  const [errors] = useState<Record<string, string>>({});
  const termsRef = useRef<HTMLDivElement>(null);
  const pdfRef = useRef<HTMLDivElement>(null);

  const [userFormData, setUserFormData] = useState<UserFormData>({
    id: 0,
    name: "",
    email: "",
    phone: "",
    membershipType: "",
    termsAndConditions: 0,
    termsAndConditionsAcceptedDate: "",
    matchId: 0,
  });

  const queryClient = useQueryClient();
  const [termsScroll, setTermsScroll] = useState(false);
  const handleScroll = useCallback(
    (
      ref: React.RefObject<HTMLDivElement | null>,
      setter: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
      if (ref.current) {
        const { scrollTop, scrollHeight, clientHeight } = ref.current;
        if (scrollTop + clientHeight >= scrollHeight - 5) {
          setter(true);
        }
      }
    },
    []
  );

  const { mutate: sendAttachmentEmail } = useSendEmailWithAttachments({
    onSuccess() {},
  });

  const {
    mutate: saveCompanyDetailsMutation,
    isPending: isCompanySavePending,
  } = useSaveCompanyProfile({
    onSuccess: () => {
      // toast.success("Investor details saved successfully");
      queryClient.invalidateQueries({ queryKey: ["company-data"] });

      navigate("/investing");
    },
    onError: () => {
      toast.error(t("common.unableProcessTryLater"));
    },
  });

  const { mutateAsync: saveTermsMutation, isPending: isTermsSavePending } =
    useSaveCompanyTerms({
      onSuccess: async () => {
        saveProfileComapnyDetails();
        toast.success(t("muslimInvest.termsAcceptedThanks"));
      },
      onError: () => {
        toast.error(t("common.unableProcessTryLater"));
      },
    });

  const { mutate: uploadCertificationPdfs } = useUploadCertificationPdfs();

  const { data: companyData } = useGetCompanyDetails({
    userId: userData?.user.id.toString() || "",
  });

  const profile = companyData ? companyData[0] : null;

  const { data: termsData } = useGetMITerms({
    userId: userData?.user.id.toString() || "",
  });
  const [fetchedTerms, setFetchedTerms] =
    useState<TermsAndConditionsData | null>(null);
  const [termsAndConditions, setTermsAndConditions] =
    useState<TermsAndConditionsData>({
      id: 0,
      isAccepted: 0,
      dateAccepted: "",
      companyProfileId: 0,
      investorProfileId: 0,
    });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors: termsErrors },
  } = useForm<CompanyTermsTypes>({
    defaultValues: {},
  });

  const saveProfileComapnyDetails = useCallback(() => {
    if (!profile) {
      toast.error(t("muslimInvest.companyProfileNotFound"));
      setLoading(false);
      return;
    }
    setLoading(true);
    const formData = new FormData();

    Object.entries(profile).forEach(([key, value]) => {
      if (key === "id" && typeof value === "number" && value <= 0) return;
      if (value !== undefined && value !== null)
        formData.append(key, String(value));
    });

    formData.set("status", "pitchInProgress");
    saveCompanyDetailsMutation(formData);
  }, [profile, saveCompanyDetailsMutation, t]);

  const handleChange = useCallback(
    (e: any, profileType: ProfileType) => {
    const { name, value, type, checked } = e.target;

    switch (profileType) {
      case ProfileType.User:
        setUserFormData({ ...userFormData, [name]: value });
        break;

      case ProfileType.TermsAndConditions:
        if (type === "checkbox") {
          if (checked) {
            setTermsAndConditions({
              ...termsAndConditions,
              isAccepted: 1,
            });
          } else {
            setTermsAndConditions({
              ...termsAndConditions,
              isAccepted: 0,
            });
          }
        }
        break;

      default:
        break;
    }
    },
    [userFormData, termsAndConditions]
  );

  useEffect(() => {
    const user = userData?.user;
    const terms = termsData && termsData[0];
    setFetchedTerms(
      terms
        ? {
            id: terms.id ?? 0,
            isAccepted: terms.isAccepted ?? 0,
            dateAccepted: terms.dateAccepted ?? "",
            companyProfileId: terms.companyProfileId ?? 0,
            investorProfileId: terms.investorProfileId ?? 0,
          }
        : null
    );

    if (user) {
      setUserFormData((prev) => ({
        ...prev,
        id: user.id ?? prev.id,
        name: String(user.firstName ?? "") + String(user.lastName ?? ""),
        email: user.email ?? prev.email,
        phone: user.businessTelephoneNumber ?? prev.phone,
        membershipType: user.membershipName ?? prev.membershipType,
        termsAndConditions: terms?.isAccepted ? 1 : 0,
        termsAndConditionsAcceptedDate:
          terms?.dateAccepted ?? prev.termsAndConditionsAcceptedDate,
        matchId: prev.matchId,
      }));
    }
  }, [termsData, userData]);

  const viewProfileCompanyDetails = useCallback(() => {
    setActiveTab((prevTab) => {
      if (prevTab !== "profile-company-details") {
        return "profile-company-details";
      }
      return prevTab;
    });
  }, []);

  const generateRiskPdf = useCallback(async () => {
    if (!pdfRef.current) return null;

    setLoading(true);

    const element = pdfRef.current.cloneNode(true) as HTMLElement;

    // Expand all scroll containers
    const expandable = element.querySelectorAll("[data-pdf-scroll]");

    expandable.forEach((div: any) => {
      div.style.overflow = "visible";
      div.style.maxHeight = "none";
      div.style.height = "auto";

      const parent = div.parentElement;
      if (parent) {
        parent.style.overflow = "visible";
        parent.style.maxHeight = "none";
        parent.style.height = "auto";
      }
    });

    try {
      const pdfBlob = await html2pdf().set(PDF_OPT).from(element).outputPdf("blob");
      return new File([pdfBlob], "company-terms.pdf", {
        type: "application/pdf",
      });
    } catch (err) {
      console.error("PDF creation failed", err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const sendMail = useCallback(async () => {
    const termsPdfAttachment = await generateRiskPdf();
    if (termsPdfAttachment) {
      sendAttachmentEmail({
        email: userFormData.email || "",
        subject: `You have accepted the SMBF / Muslim Invest Terms and Conditions`,
        name: `${userFormData.name || ""}`,
        body: `<!DOCTYPE html>
  <html lang="en">
  <head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>Terms Accepted by SMBF</title>
  </head>
  <body style="margin:0; padding:0; font-family:Arial, Helvetica, sans-serif; background-color:#f4f6f8;">
  <table width="100%" border="0" cellspacing="0" cellpadding="0" bgcolor="#f4f6f8">
    <tr>
      <td align="center" style="padding:40px 20px;">
        <table width="600" border="0" cellspacing="0" cellpadding="0" bgcolor="#ffffff"
          style="border-radius:10px; border:1px solid #e0e0e0; overflow:hidden;">
  
          <!-- Header -->
          <tr>
            <td align="center" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); padding:30px;">
              <img src="https://drive.google.com/uc?export=view&id=17bPec3b1DFm41gg757oKNyTmP1942MOg"
                alt="SMBF" width="180"
                style="max-width:180px; height:auto; display:block; margin:0 auto;" />
            </td>
          </tr>
  
          <!-- Body -->
          <tr>
            <td style="padding:35px; text-align:left; color:#333333; font-size:15px; line-height:1.6;">
              <h2 style="color:#2366CD; font-size:22px; margin:0 0 20px;">
                Assalamu Alaikum ${userFormData.name || ""},
              </h2>
              <p style="margin:0 0 15px;">
                Thank you for reviewing and accepting the <b>SMBF / Muslim Invest Terms and Conditions</b>.
              </p>
              <p style="margin:0 0 25px;">
                You may now proceed with your investment activities as per your membership status.
              </p>
              <p style="text-align:center;">
                <a href="https://www.connect.smbf.global/dashboard" target="_blank"
                  style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%);
                  color:#ffffff; padding:14px 28px; font-size:16px; text-decoration:none;
                  border-radius:6px; display:inline-block;">
                  Go to Dashboard
                </a>
              </p>
              <p style="margin:30px 0 0; font-size:13px; color:#555555;">
                Best regards,<br/>The SMBF Team
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  
    <!-- Footer -->
    <tr>
      <td align="center" style="padding:20px; font-size:11px; color:#888888;">
        &copy; 2025 SMBF. All rights reserved.
      </td>
    </tr>
  </table>
  </body>
  </html>`,
        pdfFiles: [termsPdfAttachment].filter(Boolean) as File[],
      });
    }
  }, [userFormData.email, userFormData.name, sendAttachmentEmail, generateRiskPdf]);

  const acceptTermsAndConditions = useCallback(
    async (data: CompanyTermsTypes) => {
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (key === "id" && typeof value === "number" && value <= 0) return;
        if (value !== undefined && value !== null)
          formData.append(key, String(value));
      });

      formData.append(
        "UserId",
        userData?.user.id ? userData.user.id.toString() : ""
      );
      formData.append("companyProfileId", profile ? profile?.id.toString() : "");
      formData.append("timeStamp", navigator.userAgent);
      formData.append("signedDate", Date.now().toString());

      try {
        const termsPdfAttachment = await generateRiskPdf();
        const saved = await saveTermsMutation(formData);
        const termsId = saved?.data?.id;

        if (termsId && termsPdfAttachment) {
          uploadCertificationPdfs({
            scope: "company",
            termsId,
            pdfFiles: [termsPdfAttachment],
          });
        }

        if (termsPdfAttachment) {
          sendAttachmentEmail({
            email: userFormData.email || "",
            subject: `You have accepted the SMBF / Muslim Invest Terms and Conditions`,
            name: `${userFormData.name || ""}`,
            body: `<!DOCTYPE html>
  <html lang="en">
  <head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>Terms Accepted by SMBF</title>
  </head>
  <body style="margin:0; padding:0; font-family:Arial, Helvetica, sans-serif; background-color:#f4f6f8;">
  <table width="100%" border="0" cellspacing="0" cellpadding="0" bgcolor="#f4f6f8">
    <tr>
      <td align="center" style="padding:40px 20px;">
        <table width="600" border="0" cellspacing="0" cellpadding="0" bgcolor="#ffffff"
          style="border-radius:10px; border:1px solid #e0e0e0; overflow:hidden;">
          <tr>
            <td align="center" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); padding:30px;">
              <img src="https://drive.google.com/uc?export=view&id=17bPec3b1DFm41gg757oKNyTmP1942MOg"
                alt="SMBF" width="180"
                style="max-width:180px; height:auto; display:block; margin:0 auto;" />
            </td>
          </tr>
          <tr>
            <td style="padding:35px; text-align:left; color:#333333; font-size:15px; line-height:1.6;">
              <h2 style="color:#2366CD; font-size:22px; margin:0 0 20px;">
                Assalamu Alaikum ${userFormData.name || ""},
              </h2>
              <p style="margin:0 0 15px;">
                Thank you for reviewing and accepting the <b>SMBF / Muslim Invest Terms and Conditions</b>.
              </p>
              <p style="margin:0 0 25px;">
                You may now proceed with your investment activities as per your membership status.
              </p>
              <p style="text-align:center;">
                <a href="https://www.connect.smbf.global/dashboard" target="_blank"
                  style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%);
                  color:#ffffff; padding:14px 28px; font-size:16px; text-decoration:none;
                  border-radius:6px; display:inline-block;">
                  Go to Dashboard
                </a>
              </p>
              <p style="margin:30px 0 0; font-size:13px; color:#555555;">
                Best regards,<br/>The SMBF Team
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
    <tr>
      <td align="center" style="padding:20px; font-size:11px; color:#888888;">
        &copy; 2025 SMBF. All rights reserved.
      </td>
    </tr>
  </table>
  </body>
  </html>`,
            pdfFiles: [termsPdfAttachment],
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [
      userData?.user.id,
      profile,
      saveTermsMutation,
      generateRiskPdf,
      uploadCertificationPdfs,
      sendAttachmentEmail,
      userFormData.email,
      userFormData.name,
      setLoading,
    ]
  );

  const handleTermsScroll = useCallback(() => handleScroll(termsRef, setTermsScroll), [handleScroll]);
  const handleUserChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, ProfileType.User),
    [handleChange]
  );
  const handleTermsCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      handleChange(e, ProfileType.TermsAndConditions),
    [handleChange]
  );
  const handleViewCompanyDetails = useCallback(
    () => viewProfileCompanyDetails(),
    [viewProfileCompanyDetails]
  );
  const handleNavigateCheckout = useCallback(
    () => navigate("investor-checkout"),
    [navigate]
  );
  const handleBackToCompanyDetails = useCallback(
    () => setActiveTab("profile-company-details"),
    [setActiveTab]
  );

  return (
    <div>
      {loading && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
        </div>
      )}
      <div className="dashboard content -mx-2">
        <div className="container-fluid px-0 pb-4">
          <div className="row g-0">
            <div className="col-lg-12">
              {activeTab === "profile" && (
                <div className="card shadow-sm border-0">
                  <div className="d-flex card-header bg-project-gradient text-white justify-content-between align-items-center">
                    <div className="d-flex align-items-center min-w-0">
                      <MuslimInvestFormHeaderBack />
                      <h4 className="mb-0">{shellT("profile")}</h4>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="mb-2">{shellT("name")}</label>
                      <input
                        type="text"
                        name="name"
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        placeholder={shellT("name")}
                        value={userFormData.name || ""}
                        onChange={handleUserChange}
                        disabled={true}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="mb-2">{shellT("email")}</label>
                      <input
                        type="text"
                        name="email"
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        placeholder={shellT("email")}
                        value={userFormData.email || ""}
                        onChange={handleUserChange}
                        disabled={true}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="mb-2">{shellT("phone")}</label>
                      <input
                        type="text"
                        name="phone"
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        placeholder={shellT("phone")}
                        value={userFormData.phone || ""}
                        onChange={handleUserChange}
                        disabled={true}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="mb-2">{shellT("membershipType")}</label>
                      <input
                        type="text"
                        name="membershipType"
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        placeholder={shellT("membershipType")}
                        value={userFormData.membershipType || ""}
                        onChange={handleUserChange}
                        disabled={true}
                      />
                    </div>
                    <div className="d-flex justify-content-end w-100">
                      <Button type="button" onClick={handleViewCompanyDetails}>
                        {shellT("next")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "profile-company-details" && (
                <Suspense fallback={<div style={LAZY_FALLBACK_STYLE_200} aria-hidden="true" />}>
                  <Company
                    setLoading={setLoading}
                    isEditingProfileCompanyDetails={
                      isEditingProfileCompanyDetails
                    }
                    setIsEditingProfileCompanyDetails={
                      setIsEditingProfileCompanyDetails
                    }
                    setActiveTab={setActiveTab}
                  />
                </Suspense>
              )}

              {/* Terms and Conditions */}
              {activeTab === "profile-terms-conditions" && (
                <form
                  onSubmit={handleSubmit(acceptTermsAndConditions)}
                  className="px-4"
                >
                  <div ref={pdfRef}>
                    <div className="row d-flex justify-content-center mb-4 mt-4">
                      <h4 className="text-center">
                        Please read and accept terms and conditions
                      </h4>
                    </div>

                    <div
                      ref={termsRef}
                      data-pdf-scroll="instructions"
                      style={TERMS_REF_STYLE}
                      onScroll={handleTermsScroll}
                    >
                      <Suspense fallback={<div style={LAZY_FALLBACK_STYLE_120} aria-hidden="true" />}>
                        <CompanyTerms
                          register={register}
                          setValue={setValue}
                          errors={termsErrors}
                        />
                      </Suspense>
                    </div>
                  </div>

                  <div className="mt-4 w-100">
                    {/* SHOW CHECKBOX ONLY IF TERMS NOT ACCEPTED */}
                    {userFormData.termsAndConditions === 0 ? (
                      <div className="form-check mb-2">
                        <input
                          disabled={!termsScroll}
                          className="form-check-input"
                          type="checkbox"
                          id="termsAndConditions"
                          name="termsAndConditions"
                          // checked={!!userFormData.termsAndConditions}
                          onChange={handleTermsCheckboxChange}
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="termsAndConditions"
                        >
                          I have read and accept the terms and
                          conditions
                        </label>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <label className="mb-2">
                          Terms and Conditions Accepted On
                        </label>
                        <input
                          type="text"
                          name="termsAndConditionsAcceptedDate"
                          className={`form-control ${
                            errors.name ? "is-invalid" : ""
                          }`}
                          value={
                            formatDate(
                              userFormData.termsAndConditionsAcceptedDate
                            ) || ""
                          }
                          disabled
                        />
                      </div>
                    )}

                    {/* SHOW SAVE BUTTON ONLY WHEN CHECKBOX IS CHECKED */}
                    <div className="d-flex justify-content-end">
                      {fetchedTerms?.isAccepted ? (
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          className="btn-custom-small d-flex align-items-center justify-content-center shadow-sm"
                          onClick={handleNavigateCheckout}
                        >
                          <BsFloppy size={16} className="me-2" />
                          Next
                        </Button>
                      ) : (
                        <div className="w-100 d-flex mt-1 justify-content-end gap-2">
                          <Button
                            type="button"
                            variant="light"
                            size="sm"
                            className="btn-custom-small d-flex align-items-center justify-content-center shadow-sm rounded-3 px-4 py-2"
                            onClick={handleBackToCompanyDetails}
                          >
                            Back
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            type="submit"
                            className="btn-custom-small d-flex align-items-center justify-content-center shadow-sm rounded-3 px-4 py-2 gap-2"
                            disabled={
                              termsAndConditions.isAccepted === 0 ||
                              isTermsSavePending ||
                              isCompanySavePending
                            }
                          >
                            <BsFloppy size={16} />
                            Save
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

CompanyProfile.displayName = "CompanyProfile";

export default CompanyProfile;
